/**
 * UTM Parameter and Facebook Pixel Tracking Utility
 *
 * Handles capturing UTMs, fbclid, generating _fbp, storing them in sessionStorage,
 * attempting to set standard cookies (_fbc, _fbp), and adding parameters
 * (including obfuscated FB params) to outgoing links.
 */

export const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
// Only need fbclid from URL params initially
export const FB_PARAMS_FROM_URL = ['fbclid'];

const FBCLID_SESSION_MAX_AGE_DAYS = 28; // Days the fbclid in session storage is considered valid
const FBCLID_SESSION_MAX_AGE_MS = FBCLID_SESSION_MAX_AGE_DAYS * 24 * 60 * 60 * 1000;

const COOKIE_CONFIG = {
  DOMAIN: '.lagna360.com',
  PATH: '/',
  EXPIRY_DAYS: 90 // Standard Facebook cookie expiry
};

const SECURITY_CONFIG = {
  MAX_UTM_LENGTH: 100,
  MAX_FB_LENGTH: 250, // fbclid can be long
  ALLOWED_UTM_PATTERN: /^[a-zA-Z0-9_\-.: &+%()[]{}=]+$/,
  ALLOWED_FB_PATTERN: /^[a-zA-Z0-9_\-.=]+$/ // Allow '.' and '=' for fbclid
};

/**
 * Sanitizes a parameter value to prevent security issues
 * @param {string} value - The parameter value to sanitize
 * @param {string} type - The type of parameter ('utm' or 'fb')
 * @returns {string|null} - Sanitized value or null if invalid
 */
const sanitizeValue = (value, type = 'utm') => {
  if (!value) return null;
  try {
    const maxLength = type === 'utm' ? SECURITY_CONFIG.MAX_UTM_LENGTH : SECURITY_CONFIG.MAX_FB_LENGTH;
    if (value.length > maxLength) {
      console.warn(`${type.toUpperCase()} value exceeds max length (${maxLength}):`, value.substring(0, 50) + '...');
      return null;
    }
    const maliciousPatterns = ['<', '>', 'script', 'javascript', 'data:'];
    if (maliciousPatterns.some(pattern => value.toLowerCase().includes(pattern))) {
      console.warn(`${type.toUpperCase()} value contains potentially malicious content:`, value.substring(0, 50) + '...');
      return null;
    }
    const pattern = type === 'utm' ? SECURITY_CONFIG.ALLOWED_UTM_PATTERN : SECURITY_CONFIG.ALLOWED_FB_PATTERN;
    if (!pattern.test(value)) {
      console.warn(`${type.toUpperCase()} value contains disallowed characters:`, value.substring(0, 50) + '...');
      return null;
    }
    return value;
  } catch (error) {
    console.warn(`Error sanitizing ${type.toUpperCase()} value:`, error);
    return null;
  }
};

/**
 * Sanitizes a Facebook parameter value
 * @param {string} value - The Facebook parameter value to sanitize
 * @returns {string|null} - Sanitized value or null if invalid
 */
const sanitizeFbValue = (value) => {
  return sanitizeValue(value, 'fb');
};

/**
 * Sanitizes a UTM parameter value
 * @param {string} value - The UTM parameter value to sanitize
 * @returns {string|null} - Sanitized value or null if invalid
 */
const sanitizeUtmValue = (value) => {
  return sanitizeValue(value, 'utm');
};

/**
 * Sets a cookie with the specified name, value, and options
 * @param {string} name - The name of the cookie
 * @param {string} value - The value of the cookie
 * @param {number} days - The number of days until the cookie expires (optional)
 */
const setCookie = (name, value, days = null) => {
  try {
    if (typeof window === 'undefined' || !navigator.cookieEnabled) return; // Check if cookies are enabled

    let cookieString = `${name}=${encodeURIComponent(value)}; path=${COOKIE_CONFIG.PATH}; domain=${COOKIE_CONFIG.DOMAIN}; SameSite=Lax; Secure`; // Add Secure flag

    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      cookieString += `; expires=${date.toUTCString()}`;
    }

    document.cookie = cookieString;
  } catch (error) {
    console.warn(`Error setting cookie ${name}:`, error);
  }
};

/**
 * Gets a cookie by name
 * @param {string} name - The name of the cookie to get
 * @returns {string|null} - The cookie value or null if not found
 */
const getCookie = (name) => {
  try {
    if (typeof window === 'undefined' || !navigator.cookieEnabled) return null;

    const nameEQ = encodeURIComponent(name) + "=";
    const ca = document.cookie.split(';');

    for (let i = 0; i < ca.length; i++) {
      let c = ca[i].trim();
      if (c.indexOf(nameEQ) === 0) {
        return decodeURIComponent(c.substring(nameEQ.length, c.length));
      }
    }
    return null;
  } catch (error) {
    console.warn(`Error getting cookie ${name}:`, error);
    return null;
  }
};

/**
 * Generates a Facebook Browser Pixel ID (_fbp) if one doesn't exist
 * Format: fb.[subdomainIndex].[creationTime].[randomNum]
 * @returns {string} - The generated _fbp value
 */
const generateFbp = () => {
  try {
    // Facebook's standard format: fb.[subdomainIndex].[creationTime].[randomNum]
    const creationTime = Date.now();
    const randomNum = Math.floor(Math.random() * 9000000000) + 1000000000; // 10 digit random number
    // Using '1' for subdomainIndex as per common practice
    return `fb.1.${creationTime}.${randomNum}`;
  } catch (error) {
    console.warn('Error generating _fbp value:', error);
    // Fallback, though less ideal
    return `fb.1.${Date.now()}.${Math.floor(Math.random() * 1000000000)}`;
  }
};

/**
 * Ensures the Facebook Browser Pixel ID (_fbp) exists in sessionStorage
 * and attempts to set the corresponding cookie.
 */
const ensureFbpCookie = () => {
  try {
    if (typeof window === 'undefined') return;

    let fbpValue = getCookie('_fbp');

    if (!fbpValue) {
      // If cookie doesn't exist, try checking sessionStorage first (e.g., from previous SPA navigation)
      fbpValue = sessionStorage.getItem('lagna360_fbp_value');

      if (!fbpValue) {
        // If still not found, generate a new one
        fbpValue = generateFbp();
        console.log('Generated new _fbp value:', fbpValue);
        // Attempt to set the cookie (best effort)
        setCookie('_fbp', fbpValue, COOKIE_CONFIG.EXPIRY_DAYS);
      } else {
        // Found in session storage, try setting cookie again
        setCookie('_fbp', fbpValue, COOKIE_CONFIG.EXPIRY_DAYS);
      }
    } else {
      // console.log('Existing _fbp cookie found:', fbpValue);
    }

    // Always ensure it's in sessionStorage for the URL parameter passing
    if (fbpValue) {
      sessionStorage.setItem('lagna360_fbp_value', fbpValue);
    }

  } catch (error) {
    console.warn('Error ensuring _fbp:', error);
  }
};

/**
 * Extracts the fbclid part from an _fbc cookie value
 * Handles the case where fbclid itself might contain dots
 * @param {string} fbcCookieValue - The value of the _fbc cookie
 * @returns {string|null} - The extracted fbclid or null if invalid format
 */
const extractFbclidFromCookie = (fbcCookieValue) => {
  if (!fbcCookieValue) return null;

  try {
    // _fbc format: fb.[subdomainIndex].[creationTime].[fbclid]
    const parts = fbcCookieValue.split('.');

    // We need at least 4 parts (fb, subdomainIndex, creationTime, and fbclid)
    if (parts.length < 4) return null;

    // The fbclid is everything after the third dot (index 3 onwards)
    // This correctly handles fbclid values that might contain dots
    return parts.slice(3).join('.');
  } catch (error) {
    console.warn('Error extracting fbclid from cookie:', error);
    return null;
  }
};

/**
 * Processes the Facebook Click ID (fbclid) from the URL.
 * Stores the sanitized fbclid in sessionStorage and sets the _fbc cookie
 * according to Meta's specifications.
 */
const processFbclid = () => {
  try {
    if (typeof window === 'undefined') return;

    const urlParams = new URLSearchParams(window.location.search);
    const hasFbclidInUrl = urlParams.has('fbclid');

    // Always check for existing _fbc cookie first
    const existingFbc = getCookie('_fbc');

    // Process fbclid from URL if present
    if (hasFbclidInUrl) {
      const rawFbclid = urlParams.get('fbclid');
      const sanitizedFbclid = sanitizeFbValue(rawFbclid);

      if (sanitizedFbclid) {
        const captureTime = Date.now(); // Record the time of capture
        // Store the raw sanitized fbclid in sessionStorage for URL param transfer
        sessionStorage.setItem('lagna360_fbclid', sanitizedFbclid);
        sessionStorage.setItem('lagna360_fbclid_timestamp', captureTime.toString());
        // Generate the standard _fbc value: fb.[subdomainIndex].[creationTime].[fbclid]
        // Using milliseconds for creationTime as per Meta's documentation
        const creationTime = Date.now(); // Milliseconds timestamp
        // Using '1' for subdomainIndex as recommended
        const fbcValue = `fb.1.${creationTime}.${sanitizedFbclid}`;

        // Only set the cookie if:
        // 1. _fbc cookie doesn't exist, OR
        // 2. fbclid in URL doesn't match the fbclid part in the existing cookie
        const existingFbclid = extractFbclidFromCookie(existingFbc);
        const shouldSetCookie = !existingFbc || (existingFbclid !== sanitizedFbclid);

        if (shouldSetCookie) {
          // Set the cookie with 90 days expiry as recommended by Meta
          setCookie('_fbc', fbcValue, COOKIE_CONFIG.EXPIRY_DAYS);
          console.log('Set _fbc cookie with new fbclid value');
        }
      } else {
        console.warn('Invalid fbclid in URL parameter, not storing.');
      }
    }
  } catch (error) {
    console.warn('Error processing fbclid:', error);
  }
};

/**
 * Captures UTM parameters from the URL and stores them in sessionStorage
 * Only sets parameters if they don't already exist in sessionStorage
 * This preserves the original source/medium/campaign that brought the user to the site
 */
export const captureUtmParams = () => {
  try {
    if (typeof window === 'undefined') return;

    const urlParams = new URLSearchParams(window.location.search);
    let hasUtmParams = false;

    // Process UTM parameters - only if not already set in sessionStorage
    UTM_PARAMS.forEach(param => {
      if (urlParams.has(param)) {
        // Only store if NOT already present in session
        if (!sessionStorage.getItem(param)) {
          const value = urlParams.get(param);
          const sanitizedValue = sanitizeUtmValue(value);

          if (sanitizedValue) {
            // Store in sessionStorage
            sessionStorage.setItem(param, sanitizedValue);
            hasUtmParams = true;
          }
        }
      }
    });

    // Process Facebook parameters from URL
    // Note: fbclid processing is handled in the processFbclid function
    // which uses a different logic (always capture if present)

    // Capture landing page parameter if present and not already set
    if (urlParams.has('landing') && !sessionStorage.getItem('landing')) {
      const landingValue = urlParams.get('landing');
      // Apply basic sanitization to landing value
      if (landingValue && landingValue.length < 30 && /^[a-zA-Z0-9_-]+$/.test(landingValue)) {
        sessionStorage.setItem('landing', landingValue);
      }
    }

    if (hasUtmParams) {
      console.log('UTM parameters captured from URL');
    }
  } catch (error) {
    console.warn('Error capturing UTM parameters:', error);
  }
};

/**
 * Adds UTM parameters to a URL from sessionStorage
 * @param {string} url - The URL to add parameters to
 * @param {string|null} sectionId - Optional section ID to add as utm_content
 * @returns {string} - The URL with UTM parameters added
 */
// utmTracker.js - Modify addUtmParamsToUrl

export const addUtmParamsToUrl = (url, sectionId = null) => {
  try {
    if (typeof window === 'undefined') return url;

    let urlObj;
    try {
      urlObj = new URL(url);
    } catch (urlError) {
      try {
        urlObj = new URL(url, window.location.origin);
      } catch (relativeUrlError) {
        console.warn('Invalid URL format:', url);
        return url;
      }
    }

    const params = new URLSearchParams(urlObj.search);

    // Add UTM parameters... (existing logic remains)
    UTM_PARAMS.forEach(param => {
      // ... (existing UTM logic) ...
    });
    if (sectionId) {
      params.set('utm_content', sectionId);
    }

    // Add Facebook Click ID if available AND not stale
    const fbclid = sessionStorage.getItem('lagna360_fbclid');
    const timestampStr = sessionStorage.getItem('lagna360_fbclid_timestamp');

    if (fbclid && timestampStr) {
      const timestamp = parseInt(timestampStr, 10);
      // Check if timestamp is valid and within the max age (e.g., 28 days)
      if (!isNaN(timestamp) && (Date.now() - timestamp) < FBCLID_SESSION_MAX_AGE_MS) {
        // Timestamp is valid and recent enough, add the parameter
        params.set('l360clid', fbclid); // Use the corrected name
      } else {
        // fbclid found in session is stale, don't add it.
        // Optional: Clean up stale items to prevent re-checking
        console.log('Stale fbclid found in session storage, not adding to URL.');
        sessionStorage.removeItem('lagna360_fbclid');
        sessionStorage.removeItem('lagna360_fbclid_timestamp');
        // Note: We don't remove lagna360_fbc_value as we stopped storing it.
      }
    }

    // Add Facebook Browser ID... (existing logic remains)
    const fbp = sessionStorage.getItem('lagna360_fbp_value');
    if (fbp) {
      params.set('l360p', fbp);
    }

    // Add landing page parameter... (existing logic remains)
    const landing = sessionStorage.getItem('landing');
    if (landing) {
      params.set('landing', landing);
    }

    urlObj.search = params.toString();
    return urlObj.toString();

  } catch (error) {
    console.warn('Error adding UTM parameters to URL:', error);
    return url;
  }
};

/**
 * Initializes tracking by capturing UTM parameters and processing Facebook parameters
 */
export const initializeTracking = () => {
  if (typeof window === 'undefined') return;

  console.log('Initializing tracking...');
  captureUtmParams(); // Capture UTMs first
  processFbclid();    // Process fbclid (stores in session, attempts cookie)
  ensureFbpCookie();  // Process _fbp (stores in session, attempts cookie)
};

/**
 * Retrieves UTM parameters from sessionStorage
 * @returns {Object|null} UTM parameters object or null if none exist
 */
export const getUtmParams = () => {
  if (typeof window === 'undefined') return null;

  try {
    const params = {};
    let hasParams = false;

    UTM_PARAMS.forEach(param => {
      const value = sessionStorage.getItem(param);
      if (value) {
        params[param] = value;
        hasParams = true;
      }
    });

    // Add Facebook parameters if available
    const fbclid = sessionStorage.getItem('lagna360_fbclid');
    const timestampStr = sessionStorage.getItem('lagna360_fbclid_timestamp'); // Use the consistent key

    if (fbclid && timestampStr) {
      const timestamp = parseInt(timestampStr, 10);
      // Check if timestamp is valid and within the max age
      if (!isNaN(timestamp) && (Date.now() - timestamp) < FBCLID_SESSION_MAX_AGE_MS) {
        // Timestamp is valid and recent enough, add the parameter with the correct name
        params.l360clid = fbclid; // Use the corrected name
        hasParams = true;
      }
      // If stale, we simply don't add it to the params object
    }

    const fbp = sessionStorage.getItem('lagna360_fbp_value');
    if (fbp) {
      params.l360p = fbp;
      hasParams = true;
    }

    // Add landing page parameter if available
    const landing = sessionStorage.getItem('landing');
    if (landing) {
      params.landing = landing;
      hasParams = true;
    }

    return hasParams ? params : null;
  } catch (error) {
    console.warn('Error retrieving tracking parameters:', error);
    return null;
  }
};
